import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Plus, Trash2, HardHat, X as XIcon, Undo2, AlertOctagon } from "lucide-react";
import { psaCatRank } from "../lib/psaConstants";
import PSACategoryIcon from "./PSACategoryIcon";
import PSAPickupDialog from "./psa/PSAPickupDialog";
import PSAScrapDialog from "./psa/PSAScrapDialog";
import { useAuth } from "../context/AuthContext";

// Extrage inițialele dintr-un nume complet (prenume + nume) — ex: "Khreis Christian" → "KC"
const getInitials = (first, last) => {
  const a = (first || "").trim();
  const b = (last || "").trim();
  const i1 = a ? a[0].toUpperCase() : "";
  const i2 = b ? b[0].toUpperCase() : "";
  return (i1 + i2) || "?";
};

// Culoare deterministă (hash simplu) pentru avatar-ul cu inițiale
const AVATAR_COLORS = [
  "bg-rose-500", "bg-orange-500", "bg-amber-500", "bg-lime-500",
  "bg-emerald-500", "bg-teal-500", "bg-cyan-500", "bg-sky-500",
  "bg-blue-500", "bg-indigo-500", "bg-violet-500", "bg-fuchsia-500",
  "bg-pink-500",
];
const colorForKey = (key) => {
  const s = String(key || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const PSA_LABEL = {
  masca: "Mască", furtun: "Furtun", curea: "Curea",
  casca: "Cască",
  cizme: "Cizme", tricou: "Tricou", hanorac: "Hanorac",
  geaca: "Geacă", bocanci: "Bocanci", pantaloni: "Pantaloni",
  altele: "Altele", echipament_complet: "Echipament COMPLET",
};

// Categorii respiratorii — au câmp suplimentar „model"
const RESPIRATORY_CATS = new Set(["masca", "furtun", "curea", "echipament_complet"]);
// Modelele acceptate pentru echipamentele respiratorii
const RESPIRATORY_MODELS = ["Sundström", "3M", "Altele"];

// Părțile dintr-un Echipament COMPLET — 6 obligatorii + 3 opționale (ordine: mască → furtun → curea → cizme → hanorac → tricou → bocanci → pantaloni → altele)
const COMPLETE_SET_PARTS = [
  { cat: "masca",     label: "Mască",      required: true,  hasModel: true,  hasSerial: true },
  { cat: "furtun",    label: "Furtun",     required: true,  hasModel: true,  hasSerial: true },
  { cat: "curea",     label: "Curea",      required: true,  hasModel: true,  hasSerial: true },
  { cat: "cizme",     label: "Cizme",      required: true,  hasSize: true },
  { cat: "hanorac",   label: "Hanorace",   required: true,  hasSize: true, hasQty: true },
  { cat: "tricou",    label: "Tricouri",   required: true,  hasSize: true, hasQty: true },
  { cat: "pantaloni", label: "Pantaloni",  required: false, hasSize: true, hasQty: true },
  { cat: "bocanci",   label: "Bocanci",    required: false, hasSize: true },
  { cat: "altele",    label: "Altele",     required: false, hasName: true,  hasQty: true },
];

const emptyPartsConfig = () => {
  const out = {};
  for (const p of COMPLETE_SET_PARTS) {
    out[p.cat] = {
      enabled: p.required,
      serial: "", model: "", modelCustom: "",
      size: "", qty: 1, name: "", price: "",
    };
  }
  return out;
};

// Categoriile pentru predare = catalog backend + opțiunea „echipament_complet" la început
// Ordine afișaj: mască → furtun → curea → cizme → hanorac → tricou → geacă → pantaloni → bocanci → altele
const DEFAULT_CATEGORIES = [
  { code: "echipament_complet", label: "Echipament COMPLET (Mască + Curea + Furtun)" },
  { code: "masca", label: "Mască" },
  { code: "furtun", label: "Furtun" },
  { code: "curea", label: "Curea" },
  { code: "casca", label: "Cască" },
  { code: "cizme", label: "Cizme" },
  { code: "hanorac", label: "Hanorac" },
  { code: "tricou", label: "Tricou" },
  { code: "geaca", label: "Geacă" },
  { code: "pantaloni", label: "Pantaloni" },
  { code: "bocanci", label: "Bocanci" },
  { code: "altele", label: "Altele" },
];

export default function PSAAssignmentPanel({ personId, personName = "angajat" }) {
  const { user: currentUser, isAdmin } = useAuth();
  const userRole = (currentUser?.role || "").toLowerCase();
  // Cine poate casa? admin/magazioner/planificator — orice PSA; alți useri — doar cizme.
  const canScrapAny = isAdmin || userRole === "admin" || userRole === "magazioner" || userRole === "planificator";
  const canScrapItem = (it) => canScrapAny || (it?.psa_category === "cizme");
  const [assigned, setAssigned] = useState([]);
  const [catalog, setCatalog] = useState({ categories: [], items_by_category: {} });
  // Catalog ECHIPAMENT (item_type='echipament') pentru selecție rapidă în același dialog
  const [equipCatalog, setEquipCatalog] = useState({ categories: [], items_by_category: {} });
  // Item echipament selectat pentru alocare din dropdown
  const [equipPick, setEquipPick] = useState({ itemId: "", qty: 1 });
  // Dialog: adaugă echipament NOU în inventar direct (fără să treci prin pagina Inventar)
  const [newInvOpen, setNewInvOpen] = useState(false);
  const [newInvForm, setNewInvForm] = useState({ name: "", item_type: "echipament", equipment_category: "", psa_category: "altele", psa_size: "", quantity: 1, note: "" });
  const [newInvBusy, setNewInvBusy] = useState(false);
  const [equipSearch, setEquipSearch] = useState("");
  const [psaBulkSearch, setPsaBulkSearch] = useState("");
  // Lista persoane pentru select „Alocat de" în editarea PSA
  const [allPersons, setAllPersons] = useState([]);
  // Seturi „Echipament COMPLET" deja existente (masca+furtun+curea cu același serial)
  const [completeSets, setCompleteSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  // Setup-ul rândului curent (înainte de „+ Adaugă în listă")
  const [draft, setDraft] = useState(null);
  // Lista rândurilor pregătite pentru predare în bulk
  const [staged, setStaged] = useState([]);
  const [saving, setSaving] = useState(false);
  // === Selectare RAPIDĂ multi-categorie ===
  // Per categorie: { itemId: "...", qty: 1 }. Permite alegerea unui item din fiecare categorie simultan.
  const [bulkPicks, setBulkPicks] = useState({});

  const load = async () => {
    if (!personId) return;
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get(`/persons/${personId}/psa`),
        api.get("/inventory/psa-catalog"),
        api.get("/inventory/psa-complete-sets").catch(() => ({ data: { sets: [] } })),
        api.get("/inventory/equipment-catalog").catch(() => ({ data: { categories: [], items_by_category: {} } })),
      ]);
      setAssigned(r1.data || []);
      setCatalog(r2.data || { categories: [], items_by_category: {} });
      setCompleteSets(r3.data?.sets || []);
      setEquipCatalog(r4.data || { categories: [], items_by_category: {} });
      // Fetch persons list one time (nu blochează dacă eșuează)
      if (allPersons.length === 0) {
        try {
          const { data: persons } = await api.get("/persons?limit=500");
          setAllPersons(Array.isArray(persons) ? persons : (persons?.items || []));
        } catch { /* ignore */ }
      }
    } catch (e) {
      console.error("PSA load failed", e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [personId]);

  const grouped = useMemo(() => {
    const out = {};
    for (const it of assigned) {
      const cat = it.psa_category || "altele";
      if (!out[cat]) out[cat] = [];
      out[cat].push(it);
    }
    // Returnează intrările sortate după ordinea oficială PSA (mască → furtun → curea → cizme → hanorac → tricou ...)
    const entries = Object.entries(out).sort(([a], [b]) => psaCatRank(a) - psaCatRank(b));
    return entries;
  }, [assigned]);

  const costSummary = useMemo(() => {
    let total = 0, withoutPrice = 0;
    for (const it of assigned) {
      const qty = parseFloat(it.quantity || 1) || 1;
      if (it.price_chf == null) withoutPrice += 1;
      else total += parseFloat(it.price_chf) * qty;
    }
    return { total, withoutPrice };
  }, [assigned]);

  // === Restituire cu detalii ===
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnDate, setReturnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [returnCondition, setReturnCondition] = useState("bun");
  const [returnDefectNote, setReturnDefectNote] = useState("");
  const [returnDefectFileId, setReturnDefectFileId] = useState(null);
  const [returnDefectFileName, setReturnDefectFileName] = useState("");
  const [returnBusy, setReturnBusy] = useState(false);

  const openReturn = (item) => {
    setReturnTarget(item);
    setReturnDate(new Date().toISOString().slice(0, 10));
    setReturnCondition("bun");
    setReturnDefectNote("");
    setReturnDefectFileId(null);
    setReturnDefectFileName("");
  };

  const uploadDefectImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReturnDefectFileId(data?.id || null);
      setReturnDefectFileName(file.name);
      toast.success("Imagine încărcată");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare încărcare imagine");
    }
  };

  const confirmReturn = async () => {
    if (!returnTarget) return;
    if (!returnDate) { toast.error("Specifică data restituirii"); return; }
    if (returnCondition === "defect" && !returnDefectNote.trim()) {
      toast.error("Descrie defecțiunea");
      return;
    }
    setReturnBusy(true);
    try {
      await api.post(`/inventory/${returnTarget.id}/return-with-details`, {
        return_date: returnDate,
        condition: returnCondition,
        defect_note: returnDefectNote.trim() || null,
        defect_image_file_id: returnDefectFileId || null,
      });
      toast.success(returnCondition === "defect" ? "🔴 PSA restituit ca DEFECT" : "✅ PSA restituit");
      setReturnTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la restituire");
    } finally { setReturnBusy(false); }
  };

  const restituie = (item) => openReturn(item);

  // === Adaugă ECHIPAMENT în staged din selectorul echipament ===
  const addEquipToStaged = () => {
    if (!equipPick.itemId) { toast.error("Alege un echipament"); return; }
    // Găsesc item-ul din catalog
    let found = null;
    for (const items of Object.values(equipCatalog.items_by_category || {})) {
      const it = items.find((x) => x.id === equipPick.itemId);
      if (it) { found = it; break; }
    }
    if (!found) { toast.error("Echipament negăsit"); return; }
    const qty = Math.max(1, parseFloat(equipPick.qty) || 1);
    const stockQty = parseFloat(found.quantity) || 1;
    if (qty > stockQty) { toast.error(`Doar ${stockQty} disponibile`); return; }
    setStaged((s) => [...s, {
      id: `equip-${Date.now()}`,
      cat: "echipament_inv", // marker special pentru diferențiere UI
      itemId: found.id,
      itemName: found.name,
      itemLabel: `⚙ ${found.name}${found.equipment_category ? ` · ${found.equipment_category}` : ""}`,
      qty,
      is_equipment: true,
    }]);
    setEquipPick({ itemId: "", qty: 1 });
    toast.success(`⚙ ${found.name} adăugat în listă`);
  };

  // === Creează item NOU în inventar și-l selectează automat ===
  const createNewInventoryItem = async () => {
    if (!newInvForm.name.trim()) { toast.error("Numele este obligatoriu"); return; }
    setNewInvBusy(true);
    try {
      const payload = {
        name: newInvForm.name.trim(),
        item_type: newInvForm.item_type,
        quantity: parseFloat(newInvForm.quantity) || 1,
        note: newInvForm.note?.trim() || null,
        status: "functional",
      };
      if (newInvForm.item_type === "psa") {
        payload.psa_category = newInvForm.psa_category || "altele";
        payload.psa_size = newInvForm.psa_size?.trim() || null;
      } else {
        payload.equipment_category = newInvForm.equipment_category?.trim() || null;
      }
      const { data } = await api.post("/inventory", payload);
      toast.success(`✅ „${data.name}" adăugat în inventar`);
      setNewInvOpen(false);
      setNewInvForm({ name: "", item_type: "echipament", equipment_category: "", psa_category: "altele", psa_size: "", quantity: 1, note: "" });
      // Reload cataloage să apară noul item
      await load();
      // Auto-preselecție în funcție de tipul creat
      if (data.item_type === "echipament") {
        setEquipPick({ itemId: data.id, qty: 1 });
      } else if (data.item_type === "psa" && data.psa_category) {
        updateBulkPick(data.psa_category, { itemId: data.id, qty: 1 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la creare item");
    } finally { setNewInvBusy(false); }
  };

  // === Editare PSA ===
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);

  const openEdit = (item) => {
    setEditTarget(item);
    setEditForm({
      name: item.name || "",
      psa_size: item.psa_size || "",
      serial: item.serial || "",
      supplier: item.supplier || "",
      price_chf: item.price_chf ?? "",
      note: item.note || "",
      recipient_note: item.recipient_note || "",
      quantity: item.quantity || 1,
      assigned_at: item.assigned_at ? String(item.assigned_at).slice(0, 10) : "",
      assigned_by_id: item.assigned_by_id || "",
      assigned_by: item.assigned_by || "",
    });
  };

  const confirmEdit = async () => {
    if (!editTarget) return;
    if (!editForm.name?.trim()) { toast.error("Numele este obligatoriu"); return; }
    setEditBusy(true);
    try {
      // Trimit body complet cu overrides din editForm
      const payload = {
        ...editTarget,
        name: editForm.name.trim(),
        psa_size: editForm.psa_size?.trim() || null,
        serial: editForm.serial?.trim() || null,
        supplier: editForm.supplier?.trim() || null,
        price_chf: editForm.price_chf === "" ? null : parseFloat(editForm.price_chf),
        note: editForm.note?.trim() || null,
        recipient_note: editForm.recipient_note?.trim() || null,
        quantity: parseFloat(editForm.quantity) || 1,
        // Data alocării — dacă e goală păstrăm valoarea veche, altfel setăm noua dată (ISO)
        assigned_at: editForm.assigned_at
          ? new Date(editForm.assigned_at + "T00:00:00").toISOString()
          : (editTarget.assigned_at || null),
        // Cine a alocat: dacă utilizatorul a schimbat, aplicăm noua persoană (id + nume)
        assigned_by_id: editForm.assigned_by_id || editTarget.assigned_by_id || null,
        assigned_by: editForm.assigned_by || editTarget.assigned_by || null,
      };
      // Curăț câmpurile care nu aparțin de InventoryIn (evitare erori Pydantic)
      delete payload.id;
      delete payload._id;
      // NU șterg assigned_at / assigned_by / assigned_by_id — trebuie păstrate/modificate prin editare
      delete payload.returned_at;
      delete payload.returned_by_id;
      delete payload.last_return_condition;
      delete payload.last_return_defect_note;
      delete payload.last_return_defect_image_file_id;
      delete payload.holder_name;
      await api.put(`/inventory/${editTarget.id}`, payload);
      toast.success("PSA actualizat");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setEditBusy(false); }
  };

  // === Istoric PSA per item ===
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (item) => {
    setHistoryTarget(item);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/inventory/${item.id}/history`);
      setHistoryRows(Array.isArray(data) ? data : []);
    } catch {
      setHistoryRows([]);
      toast.error("Eroare la încărcare istoric");
    } finally { setHistoryLoading(false); }
  };

  // === Casare PSA ===
  const SCRAP_REASONS = [
    { code: "uzura", label: "Uzură normală — sfârșit de viață" },
    { code: "defect", label: "Defect ireparabil" },
    { code: "pierdere", label: "Pierdut / nereturnat" },
    { code: "contaminat", label: "Contaminat — nu mai e folosibil" },
    { code: "altele", label: "Alt motiv (specifică în notă)" },
  ];
  const [scrapTarget, setScrapTarget] = useState(null);
  const [scrapReason, setScrapReason] = useState("uzura");
  const [scrapNote, setScrapNote] = useState("");
  const [scrapDate, setScrapDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [scrapBusy, setScrapBusy] = useState(false);

  const openScrap = (item) => {
    setScrapTarget(item);
    setScrapReason("uzura");
    setScrapNote("");
    setScrapDate(new Date().toISOString().slice(0, 10));
  };
  const confirmScrap = async () => {
    if (!scrapTarget) return;
    if (!scrapDate) { toast.error("Specifică data casării"); return; }
    setScrapBusy(true);
    try {
      const reasonLabel = SCRAP_REASONS.find((r) => r.code === scrapReason)?.label || scrapReason;
      await api.post(`/inventory/${scrapTarget.id}/scrap`, {
        reason: reasonLabel,
        note: scrapNote.trim() || null,
        operation_date: scrapDate,
      });
      toast.success(`✓ ${scrapTarget.name} casat`);
      setScrapTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la casare");
    } finally {
      setScrapBusy(false);
    }
  };

  const openAddDialog = () => {
    setStaged([]);
    setBulkPicks({});
    setDraft({
      mode: "existing", cat: "", itemId: "", serial: "", size: "",
      price: "", model: "", modelCustom: "", note: "",
      parts: emptyPartsConfig(), // pentru echipament_complet
      completeSetId: "", // selected existing set (Sundström #52, etc.)
    });
    setAddOpen(true);
  };

  const resetDraft = () => {
    setDraft({
      mode: "existing", cat: "", itemId: "", serial: "", size: "",
      price: "", model: "", modelCustom: "", note: "",
      qty: 1, // cantitate cerută din stoc (split-assign pentru PSA cu mărime)
      parts: emptyPartsConfig(),
      completeSetId: "",
    });
  };

  const updatePart = (cat, patch) => {
    setDraft({ ...draft, parts: { ...draft.parts, [cat]: { ...draft.parts[cat], ...patch } } });
  };

  // Categoriile disponibile (pentru selector)
  const categories = useMemo(() => {
    // Fallback la default dacă catalog-ul backend nu are echipament_complet
    const codes = new Set((catalog.categories || []).map((c) => c.code));
    const out = [...DEFAULT_CATEGORIES];
    // Adaugă din catalog cele care nu sunt în default
    for (const c of (catalog.categories || [])) {
      if (!out.find((x) => x.code === c.code)) out.push({ code: c.code, label: `${c.icon || "📦"} ${c.label}` });
    }
    return out;
  }, [catalog]);

  const availableForCat = (catalog.items_by_category || {})[draft?.cat] || [];

  const addDraftToStaged = () => {
    if (!draft.cat) { toast.error("Alege categoria PSA"); return; }
    // CASE SPECIAL: Echipament COMPLET → multiple piese în staged
    if (draft.cat === "echipament_complet") {
      // SUB-CASE: utilizatorul a ales un SET existent din inventar (ex: Sundström #52)
      if (draft.completeSetId) {
        const set = completeSets.find((s) => s.set_id === draft.completeSetId);
        if (!set) { toast.error("Setul ales nu mai e disponibil"); return; }
        const partsToAdd = [
          { id: set.masca_id, cat: "masca", label: set.masca_name, serial: set.serial, size: set.masca_size },
          { id: set.furtun_id, cat: "furtun", label: set.furtun_name, serial: set.serial, size: "" },
          { id: set.curea_id, cat: "curea", label: set.curea_name, serial: set.serial, size: "" },
        ].map((p) => ({
          mode: "existing",
          cat: p.cat,
          itemId: p.id,
          itemName: p.label,
          serial: p.serial,
          size: p.size || "",
          price: "",
          model: set.model || "",
          note: `Parte din Echipament COMPLET nr ${set.serial}`,
          fromCompleteSet: true,
          qtyIndex: "",
          _id: Date.now() + Math.random() + p.id,
        }));
        setStaged([...staged, ...partsToAdd]);
        resetDraft();
        toast.success(`✓ Set ${set.model} #${set.serial} adăugat (3 piese existente)`);
        return;
      }
      const partsToAdd = [];
      for (const p of COMPLETE_SET_PARTS) {
        const pState = draft.parts[p.cat];
        if (!pState.enabled) continue;
        // Skip automat dacă piesa are cantitate și user a pus 0 sau "fără"
        if (p.hasQty) {
          const qtyStr = String(pState.qty || "").trim().toLowerCase();
          const qtyNum = parseInt(qtyStr);
          if (qtyStr === "" || qtyStr === "0" || qtyStr === "fara" || qtyStr === "fără" || qtyStr === "nu" || qtyNum === 0) {
            // Piesa NU se predă — sari peste fără validare
            continue;
          }
        }
        // Validări per parte
        if (p.hasSerial && !pState.serial.trim()) {
          toast.error(`${p.label}: introdu nr. de serie (sau scrie "0" la cantitate ca să sari peste)`); return;
        }
        if (p.hasModel) {
          if (!pState.model) { toast.error(`${p.label}: alege modelul (Sundström/3M/Altele)`); return; }
          if (pState.model === "Altele" && !pState.modelCustom.trim()) {
            toast.error(`${p.label}: introdu modelul custom`); return;
          }
        }
        if (p.hasSize && !pState.size.trim()) {
          toast.error(`${p.label}: introdu măsura (sau debifează piesa dacă nu se predă)`); return;
        }
        if (p.hasName && !pState.name.trim()) {
          toast.error(`${p.label}: introdu denumirea produsului`); return;
        }
        const finalModel = pState.model === "Altele" ? pState.modelCustom.trim() : pState.model;
        const qty = p.hasQty ? Math.max(1, parseInt(pState.qty || 1)) : 1;
        // Pentru categorii cu cantitate, generez N items separați (cu sufix #1, #2 etc)
        for (let i = 0; i < qty; i++) {
          partsToAdd.push({
            mode: "new",
            cat: p.cat,
            serial: p.hasSerial ? pState.serial : "",
            customName: p.hasName ? pState.name : "",
            size: pState.size || "",
            price: pState.price || "",
            model: finalModel || "",
            note: `Parte din Echipament COMPLET`,
            fromCompleteSet: true,
            qtyIndex: p.hasQty && qty > 1 ? `#${i + 1}` : "",
            _id: Date.now() + Math.random() + i,
          });
        }
      }
      if (partsToAdd.length === 0) { toast.error("Activează cel puțin o piesă"); return; }
      setStaged([...staged, ...partsToAdd]);
      resetDraft();
      toast.success(`${partsToAdd.length} piese adăugate în listă din Echipamentul COMPLET`);
      return;
    }
    // Normal cases
    if (draft.mode === "existing") {
      if (!draft.itemId) { toast.error("Selectează un produs din inventar"); return; }
    } else {
      if (!draft.serial.trim()) { toast.error("Introdu nr. de serie pentru item-ul nou"); return; }
    }
    if (RESPIRATORY_CATS.has(draft.cat) && !draft.model) {
      toast.error("Alege modelul echipamentului respirator (Sundström / 3M / Altele)");
      return;
    }
    if (draft.model === "Altele" && !draft.modelCustom.trim()) {
      toast.error('Introdu modelul (când ai ales "Altele")');
      return;
    }
    const finalModel = draft.model === "Altele" ? draft.modelCustom.trim() : draft.model;
    setStaged([...staged, { ...draft, model: finalModel, _id: Date.now() + Math.random() }]);
    resetDraft();
    toast.success('Adăugat în listă. Adaugă altul sau apasă "Predă toate".');
  };

  const removeFromStaged = (id) => {
    setStaged(staged.filter((s) => s._id !== id));
  };

  const submitAll = async () => {
    if (staged.length === 0) {
      // Dacă nu există nimic în staged dar draft-ul are conținut, încearcă să-l adauge automat
      if (draft && (draft.itemId || draft.serial)) {
        toast.info('Apasă "+ Adaugă în listă" mai întâi');
        return;
      }
      toast.error("Nimic de predat");
      return;
    }
    setSaving(true);
    let totalCreated = 0;
    let totalAssigned = 0;
    try {
      for (const s of staged) {
        if (s.is_equipment) {
          // ECHIPAMENT existent din inventar — split-assign dacă stoc > 1 și qty < stoc, altfel move complet
          const reqQty = parseFloat(s.qty) || 1;
          let stockItem = null;
          for (const arr of Object.values(equipCatalog.items_by_category || {})) {
            stockItem = arr.find((it) => it.id === s.itemId);
            if (stockItem) break;
          }
          const stockQty = parseFloat(stockItem?.quantity) || 1;
          if (stockQty > 1 && reqQty < stockQty) {
            await api.post(`/inventory/${s.itemId}/split-assign`, {
              person_id: personId,
              qty: reqQty,
              recipient_note: `Predare echipament către ${personName}`,
              reason: `Predare echipament (split ${reqQty} din ${stockQty})`,
            });
          } else {
            await api.post(`/inventory/${s.itemId}/move`, {
              assignee_person_id: personId,
              recipient_note: `Predare echipament către ${personName}`,
              reason: "Predare echipament PSA/inventar",
            });
          }
          totalAssigned++;
          continue;
        }
        if (s.cat === "echipament_complet") {
          // Backwards compat: legacy single-staged echipament_complet (înainte nu existau parts)
          const subs = [
            { cat: "masca", label: "Mască" },
            { cat: "curea", label: "Curea" },
            { cat: "furtun", label: "Furtun" },
          ];
          for (const sub of subs) {
            const payload = {
              name: `${sub.label} ${s.serial}${s.model ? ` (${s.model})` : ""}`,
              item_type: "psa", psa_category: sub.cat, psa_size: s.size || "",
              serial: s.serial, no_serial: false, quantity: 1, unit: "buc",
              status: "functional", location: "MAGAZIE", supplier: s.model || "",
            };
            if (s.price && parseFloat(s.price) > 0) payload.price_chf = parseFloat(s.price);
            const created = await api.post("/inventory", payload);
            const newId = created.data?.id;
            if (!newId) throw new Error("Item creat fără id");
            await api.post(`/inventory/${newId}/move`, {
              assignee_person_id: personId,
              recipient_note: s.note || `Predat set complet către ${personName}`,
              reason: s.note || "Predare set complet PSA",
            });
            totalCreated++; totalAssigned++;
          }
        } else if (s.fromCompleteSet) {
          // Item din echipament complet (cu nume custom sau cu serial)
          let baseName;
          if (s.customName) {
            baseName = `${s.customName}${s.qtyIndex ? ` ${s.qtyIndex}` : ""}`;
          } else {
            const catLabel = PSA_LABEL[s.cat] || s.cat;
            baseName = `${catLabel.replace(/^[^ ]+ /, "")} ${s.serial || ""}${s.qtyIndex ? ` ${s.qtyIndex}` : ""}`.trim();
            if (s.model) baseName += ` (${s.model})`;
          }
          const payload = {
            name: baseName, item_type: "psa", psa_category: s.cat,
            psa_size: s.size || "", serial: s.serial || "",
            no_serial: !s.serial, quantity: 1, unit: "buc",
            status: "functional", location: "MAGAZIE", supplier: s.model || "",
          };
          if (s.price && parseFloat(s.price) > 0) payload.price_chf = parseFloat(s.price);
          const created = await api.post("/inventory", payload);
          const newId = created.data?.id;
          if (!newId) throw new Error("Item creat fără id");
          await api.post(`/inventory/${newId}/move`, {
            assignee_person_id: personId,
            recipient_note: s.note || `Predat ca parte din Echipament COMPLET către ${personName}`,
            reason: s.note || "Predare Echipament COMPLET",
          });
          totalCreated++; totalAssigned++;
        } else if (s.mode === "existing") {
          // Detectează dacă e PSA cu mărime și qty > 1 → folosește split-assign
          const reqQty = parseFloat(s.qty) || 1;
          const isRespiratory = ["masca", "furtun", "curea"].includes(s.cat);
          const stockItem = (catalog.items_by_category?.[s.cat] || []).find((it) => it.id === s.itemId);
          const stockQty = parseFloat(stockItem?.quantity) || 1;
          if (!isRespiratory && stockQty > 1 && reqQty < stockQty) {
            // Split-assign: scade reqQty din stoc, creează item nou asignat persoanei
            await api.post(`/inventory/${s.itemId}/split-assign`, {
              person_id: personId,
              quantity: reqQty,
              note: s.note || `Predate ${reqQty} buc. către ${personName}`,
            });
          } else {
            // Cantitate completă sau item respiratoriu (unic) → asignează tot itemul
            await api.post(`/inventory/${s.itemId}/move`, {
              assignee_person_id: personId,
              recipient_note: s.note || `Predat către ${personName}`,
              reason: s.note || "Predare PSA angajat",
            });
          }
          totalAssigned++;
        } else {
          // Mod „new" — caută existent după serial ȘI categorie (același serial poate exista
          // pentru categorii diferite — mască 1, curea 1, furtun 1 sunt obiecte distincte)
          const allInv = await api.get("/inventory").catch(() => ({ data: [] }));
          const existing = (allInv.data || []).find(
            (it) => it.item_type === "psa"
              && (it.psa_category || "") === (s.cat || "")
              && (it.serial || "").trim().toLowerCase() === s.serial.trim().toLowerCase()
          );
          let targetId;
          if (existing) {
            if (existing.assignee_person_id && existing.assignee_person_id !== personId) {
              toast.warning(`SN „${s.serial}" e deja la altcineva. Skip.`);
              continue;
            }
            targetId = existing.id;
          } else {
            const payload = {
              name: `${PSA_LABEL[s.cat] || s.cat} ${s.serial}${s.model ? ` (${s.model})` : ""}`,
              item_type: "psa",
              psa_category: s.cat,
              psa_size: s.size || "",
              serial: s.serial,
              no_serial: false,
              quantity: 1,
              unit: "buc",
              status: "functional",
              location: "MAGAZIE",
              supplier: s.model || "",
            };
            if (s.price && parseFloat(s.price) > 0) payload.price_chf = parseFloat(s.price);
            const created = await api.post("/inventory", payload);
            targetId = created.data?.id;
            if (!targetId) throw new Error("Item creat fără id");
            totalCreated++;
          }
          await api.post(`/inventory/${targetId}/move`, {
            assignee_person_id: personId,
            recipient_note: s.note || `Predat către ${personName}`,
            reason: s.note || "Predare PSA + creare auto în inventar",
          });
          totalAssigned++;
        }
      }
      toast.success(`Predat ${totalAssigned} item${totalAssigned === 1 ? "" : "e"}${totalCreated > 0 ? ` (din care ${totalCreated} create în inventar)` : ""}`);
      setAddOpen(false);
      setStaged([]);
      setBulkPicks({});
      resetDraft();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Eroare la predare în bulk");
    } finally { setSaving(false); }
  };

  // === Bulk multi-pick: adăugare simultană din mai multe categorii ===
  const updateBulkPick = (cat, patch) => {
    setBulkPicks((prev) => ({ ...prev, [cat]: { ...(prev[cat] || {}), ...patch } }));
  };
  const addBulkPicksToStaged = () => {
    const newRows = [];
    for (const cat of Object.keys(bulkPicks)) {
      const pick = bulkPicks[cat];
      if (!pick?.itemId) continue;
      const items = (catalog.items_by_category || {})[cat] || [];
      const it = items.find((x) => x.id === pick.itemId);
      if (!it) continue;
      const reqQty = parseInt(pick.qty, 10) || 1;
      newRows.push({
        mode: "existing",
        cat,
        itemId: it.id,
        serial: it.serial || "",
        size: it.psa_size || "",
        price: it.price_chf != null ? String(it.price_chf) : "",
        model: it.supplier || "",
        note: "",
        qty: Math.max(1, reqQty),
        _location: it.location || "",
        _id: Date.now() + Math.random() + cat,
      });
    }
    if (newRows.length === 0) {
      toast.error("Selectează cel puțin un produs din inventar");
      return;
    }
    setStaged((prev) => [...prev, ...newRows]);
    setBulkPicks({});
    toast.success(`✓ ${newRows.length} item${newRows.length === 1 ? "" : "e"} adăugat${newRows.length === 1 ? "" : "e"} în coadă`);
  };

  // === Bulk multi-pick: adăugare simultană din mai multe categorii ===
  const [persons, setPersons] = useState([]);
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    api.get("/persons").then((r) => setPersons((r.data || []).filter((p) => p.active !== false))).catch(() => setPersons([]));
    api.get("/inventory/meta").then((r) => setLocations(r.data?.locations || [])).catch(() => setLocations([]));
  }, []);

  // === PICKUP: preluare PSA de la angajat ===
  const [pickupOpen, setPickupOpen] = useState(false);
  // Map: item_id → { selected: bool, dest: "loc:NAME" | "person:PID" | "" }
  const [pickupTargets, setPickupTargets] = useState({});
  const [pickupBulkDest, setPickupBulkDest] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [pickupBy, setPickupBy] = useState("");
  const [pickupBusy, setPickupBusy] = useState(false);

  const openPickup = () => {
    const init = {};
    for (const it of assigned) init[it.id] = { selected: false, dest: "" };
    setPickupTargets(init);
    setPickupBulkDest("");
    setPickupDate(new Date().toISOString().slice(0, 10));
    setPickupBy("");
    setPickupOpen(true);
  };
  const updatePickupRow = (iid, patch) => {
    setPickupTargets((prev) => ({ ...prev, [iid]: { ...(prev[iid] || {}), ...patch } }));
  };
  const applyPickupBulkDest = () => {
    if (!pickupBulkDest) { toast.error("Alege destinația comună"); return; }
    setPickupTargets((prev) => {
      const next = { ...prev };
      for (const iid of Object.keys(next)) {
        if (next[iid].selected) next[iid] = { ...next[iid], dest: pickupBulkDest };
      }
      return next;
    });
    toast.success("Destinația aplicată pe articolele selectate");
  };
  const selectAllPickup = (val) => {
    setPickupTargets((prev) => {
      const next = { ...prev };
      for (const iid of Object.keys(next)) next[iid] = { ...next[iid], selected: val };
      return next;
    });
  };
  const submitPickup = async () => {
    const rows = Object.entries(pickupTargets).filter(([_, v]) => v?.selected);
    if (rows.length === 0) { toast.error("Selectează cel puțin un articol"); return; }
    const missing = rows.filter(([_, v]) => !v.dest);
    if (missing.length > 0) {
      toast.error(`${missing.length} articol${missing.length === 1 ? "" : "e"} fără destinație`);
      return;
    }
    setPickupBusy(true);
    const pickupPerson = persons.find((p) => p.id === pickupBy);
    const pickedUpBy = pickupPerson ? `${pickupPerson.nume || ""} ${pickupPerson.prenume || ""}`.trim() : null;
    let okCount = 0, errCount = 0;
    try {
      for (const [iid, v] of rows) {
        const item = assigned.find((a) => a.id === iid);
        try {
          if ((v.dest || "").startsWith("person:")) {
            const targetPid = v.dest.slice("person:".length);
            await api.post(`/inventory/${iid}/move`, {
              location: item?.location || "MAGAZIE",
              assignee_person_id: targetPid,
              reason: `Transfer PSA de la ${personName}`,
              operation_date: pickupDate || null,
              picked_up_by: pickedUpBy,
              picked_up_by_id: pickupBy || null,
            });
          } else if ((v.dest || "").startsWith("loc:")) {
            const targetLoc = v.dest.slice("loc:".length);
            await api.post(`/inventory/${iid}/move`, {
              location: targetLoc,
              assignee_clear: true,
              reason: `Preluare PSA de la ${personName} → ${targetLoc}`,
              operation_date: pickupDate || null,
              picked_up_by: pickedUpBy,
              picked_up_by_id: pickupBy || null,
            });
          }
          okCount++;
        } catch (e) {
          console.error("pickup row failed", iid, e);
          errCount++;
        }
      }
      if (okCount > 0) toast.success(`✓ ${okCount} PSA preluat${okCount === 1 ? "" : "e"}${errCount > 0 ? ` · ${errCount} cu erori` : ""}`);
      else toast.error("Nicio operațiune nu a reușit");
      setPickupOpen(false);
      load();
    } finally { setPickupBusy(false); }
  };

  if (!personId) {
    return (
      <div className="p-3 rounded-md bg-zinc-50 border border-zinc-200 text-xs text-zinc-600" data-testid="psa-panel-empty">
        💡 Salvează persoana întâi, apoi vei putea preda PSA din inventar.
      </div>
    );
  }

  const isCompleteSet = draft?.cat === "echipament_complet";
  const showModelField = draft?.cat && RESPIRATORY_CATS.has(draft.cat);

  return (
    <div className="space-y-2.5" data-testid="psa-assignment-panel">
      {/* Header cu cost total */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <HardHat size={16} className="text-cyan-700" />
          <span className="text-sm font-bold uppercase tracking-wider text-cyan-900">Echipament PSA</span>
          <span className="text-xs text-zinc-500">({assigned.length})</span>
          {assigned.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-900 font-bold" data-testid="psa-total-cost">
              💰 {costSummary.total.toFixed(2)} CHF
              {costSummary.withoutPrice > 0 && <span className="ml-1 text-[10px] text-amber-700">· {costSummary.withoutPrice} fără preț</span>}
            </span>
          )}
        </div>
        <Button type="button" size="sm" onClick={openAddDialog} className="bg-cyan-600 hover:bg-cyan-700" data-testid="psa-add-btn">
          <Plus size={14} className="mr-1"/> Predă PSA
        </Button>
        {assigned.length > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={openPickup}
            className="border-orange-400 text-orange-800 hover:bg-orange-50"
            data-testid="psa-pickup-btn"
          >
            <Undo2 size={14} className="mr-1"/> Preia PSA
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500 italic py-2">Se încarcă PSA...</div>
      ) : assigned.length === 0 ? (
        <div className="text-xs text-zinc-500 italic py-2 px-3 rounded bg-zinc-50 border border-zinc-200" data-testid="psa-none">
          Niciun PSA predat încă. Apasă „Predă PSA" pentru a aloca echipament din inventar.
        </div>
      ) : (
        <div className="space-y-2">
          {grouped.map(([cat, items]) => (
            <div key={cat} className="border border-cyan-200 rounded-md overflow-hidden" data-testid={`psa-group-${cat}`}>
              <div className="bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-900 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5">
                  <PSACategoryIcon cat={cat} size={16} className="text-cyan-800"/>
                  {PSA_LABEL[cat] || cat}
                </span>
                <span className="text-[10px] text-cyan-700">{items.length} buc.</span>
              </div>
              <ul className="divide-y divide-cyan-100">
                {items.map((it) => (
                  <li key={it.id} className="px-2.5 py-1.5 flex items-center justify-between gap-2 text-xs hover:bg-cyan-50/40" data-testid={`psa-item-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold">{it.name}</span>
                        {it.psa_size && <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-bold text-[10px]">măs. {it.psa_size}</span>}
                        {it.serial && <span className="font-mono text-[10px] text-zinc-500">SN: {it.serial}</span>}
                        {it.supplier && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">model: {it.supplier}</span>}
                        {it.quantity > 1 && <span className="text-[10px] text-zinc-600">{it.quantity}{it.unit ? " " + it.unit : ""}</span>}
                        {it.price_chf != null && (
                          <span className="text-[10px] text-emerald-700 font-semibold">{(parseFloat(it.price_chf) * (parseFloat(it.quantity) || 1)).toFixed(2)} CHF</span>
                        )}
                      </div>
                      {it.recipient_note && (
                        <div className="text-[10px] mt-0.5">
                          <span className="text-zinc-500 italic">📝 {it.recipient_note}</span>
                          {it.assigned_by && (
                            <span className="ml-1.5 not-italic">
                              · <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">👤 alocat de {it.assigned_by}</span>
                            </span>
                          )}
                        </div>
                      )}
                      {/* Audit info: cine și când a alocat + info restituire dacă a fost defect */}
                      {(it.assigned_at || (it.assigned_by && !it.recipient_note)) && (
                        <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                          <span>📅</span>
                          {it.assigned_at && <span>Alocat: <b className="text-zinc-700">{new Date(it.assigned_at).toLocaleDateString("ro-RO")}</b></span>}
                          {it.assigned_by && !it.recipient_note && <span>de <b className="text-zinc-700">{it.assigned_by}</b></span>}
                          {!it.assigned_by && (
                            <span className="text-amber-600 italic">· alocat de: necunoscut (apasă ✏️ pentru a seta)</span>
                          )}
                        </div>
                      )}
                      {it.last_return_condition === "defect" && it.last_return_defect_note && (
                        <div className="text-[10px] text-red-700 mt-0.5 bg-red-50 border border-red-200 rounded px-1.5 py-1 flex items-start gap-1.5">
                          <span className="font-bold">🔴 Restituit ca DEFECT:</span>
                          <div className="flex-1">
                            <div>{it.last_return_defect_note}</div>
                            {it.last_return_defect_image_file_id && (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.last_return_defect_image_file_id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-block mt-1"
                                title="Vezi imaginea defectului"
                              >
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.last_return_defect_image_file_id}`}
                                  alt="defect"
                                  className="max-h-16 rounded border border-red-300"
                                />
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button type="button" onClick={() => openEdit(it)} className="text-zinc-600 hover:text-zinc-900 text-[11px] px-1.5 py-0.5 hover:bg-zinc-100 rounded flex items-center gap-1" title="Editează" data-testid={`psa-edit-${it.id}`}>
                        ✏️
                      </button>
                      <button type="button" onClick={() => openHistory(it)} className="text-indigo-600 hover:text-indigo-900 text-[11px] px-1.5 py-0.5 hover:bg-indigo-50 rounded flex items-center gap-1" title="Istoric" data-testid={`psa-history-${it.id}`}>
                        📜
                      </button>
                      <button type="button" onClick={() => restituie(it)} className="text-blue-600 hover:text-blue-800 text-[11px] px-1.5 py-0.5 hover:bg-blue-50 rounded flex items-center gap-1" data-testid={`psa-restituie-${it.id}`}>
                        <Undo2 size={11}/> Restituie
                      </button>
                      {canScrapItem(it) && (
                        <button
                          type="button"
                          onClick={() => openScrap(it)}
                          className="text-red-700 hover:text-red-900 text-[11px] px-1.5 py-0.5 hover:bg-red-50 rounded flex items-center gap-1 border border-red-200"
                          data-testid={`psa-casare-${it.id}`}
                          title={canScrapAny ? "Casare definitivă" : "Casare cizme (doar pentru tine)"}
                        >
                          <AlertOctagon size={11}/> Casează
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Dialog: Predă PSA (multi-add cu setup, staged, model) */}
      <Dialog open={addOpen} onOpenChange={(v) => { if (!saving) setAddOpen(v); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="psa-add-dialog">
          <DialogHeader>
            <DialogTitle>🦺 Predă PSA din inventar — multiple items</DialogTitle>
          </DialogHeader>

          {/* === SELECTARE RAPIDĂ multi-categorie din INVENTAR === */}
          {(() => {
            // Categorii relevante pentru multi-pick (fără echipament_complet și altele care necesită config special)
            const PICK_CATS = ["masca", "furtun", "curea", "casca", "cizme", "tricou", "hanorac", "geaca", "pantaloni", "bocanci"];
            const available = catalog.items_by_category || {};
            const totalPickable = PICK_CATS.reduce((s, c) => s + (available[c]?.length || 0), 0);
            if (totalPickable === 0) return null;
            const selectedCount = Object.values(bulkPicks).filter((p) => p?.itemId).length;
            return (
              <div className="border-2 border-cyan-400 rounded-md p-3 bg-gradient-to-br from-cyan-50 to-emerald-50 space-y-2" data-testid="psa-bulk-pick-block">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs font-bold uppercase tracking-wider text-cyan-900">
                    🚀 Selectare rapidă din inventar — alege simultan din mai multe categorii
                  </div>
                  {selectedCount > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-200 text-cyan-900 font-bold">
                      {selectedCount} selectate
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-cyan-800 italic">
                  Pentru fiecare categorie poți alege un produs existent (cu locația vizibilă). Apasă „Adaugă tot în coadă" pentru a le pune pe toate odată.
                </div>
                <div className="relative">
                  <Input
                    type="text"
                    value={psaBulkSearch}
                    onChange={(e) => setPsaBulkSearch(e.target.value)}
                    placeholder="🔍 Caută în toate PSA (nume, serial, mărime)..."
                    className="h-7 text-xs pr-6 bg-white"
                    data-testid="psa-bulk-search"
                  />
                  {psaBulkSearch && (
                    <button type="button" onClick={() => setPsaBulkSearch("")} className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 text-xs">✕</button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PICK_CATS.map((cat) => {
                    const rawItems = available[cat] || [];
                    const q = psaBulkSearch.trim().toLowerCase();
                    const items = !q ? rawItems : rawItems.filter((it) => {
                      const hay = `${it.name || ""} ${it.serial || ""} ${it.psa_size || ""} ${it.location || ""}`.toLowerCase();
                      return hay.includes(q);
                    });
                    if (items.length === 0) return null;
                    const pick = bulkPicks[cat] || {};
                    const selected = items.find((it) => it.id === pick.itemId);
                    const stockQty = parseFloat(selected?.quantity) || 0;
                    const isRespiratory = ["masca", "furtun", "curea"].includes(cat);
                    return (
                      <div
                        key={cat}
                        className={`p-2 rounded border ${selected ? "bg-white border-cyan-400 shadow-sm" : "bg-white/50 border-zinc-200"}`}
                        data-testid={`psa-bulk-cat-${cat}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <PSACategoryIcon cat={cat} size={16} className="text-cyan-700"/>
                          <Label className="text-xs font-bold text-cyan-900">{PSA_LABEL[cat] || cat}</Label>
                          <span className="text-[10px] text-zinc-500">({items.length} disp.)</span>
                        </div>
                        <Select
                          value={pick.itemId || "__none__"}
                          onValueChange={(v) => updateBulkPick(cat, { itemId: v === "__none__" ? "" : v, qty: 1 })}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`psa-bulk-pick-${cat}`}>
                            <SelectValue placeholder="— Alege produsul —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Niciun produs —</SelectItem>
                            {items.map((it) => {
                              const qty = parseFloat(it.quantity) || 1;
                              const stockLabel = qty > 1 ? ` · 📦 ${qty}` : "";
                              const locLabel = it.location ? ` · 📍 ${it.location}` : "";
                              return (
                                <SelectItem key={it.id} value={it.id}>
                                  {it.name}{it.psa_size ? ` · ${it.psa_size}` : ""}{it.serial ? ` · SN ${it.serial}` : ""}{stockLabel}{locLabel}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {selected && (
                          <div className="mt-1.5 text-[10px] flex items-center gap-2 flex-wrap">
                            {selected.location && (
                              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-900 font-semibold">
                                📍 {selected.location}
                              </span>
                            )}
                            {selected.psa_size && (
                              <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-semibold">
                                măs. {selected.psa_size}
                              </span>
                            )}
                            {selected.serial && (
                              <span className="font-mono text-zinc-500">SN: {selected.serial}</span>
                            )}
                            {!isRespiratory && stockQty > 1 && (
                              <div className="flex items-center gap-1 ml-auto">
                                <span className="text-zinc-600">Cant.:</span>
                                <Input
                                  type="number"
                                  min="1"
                                  max={stockQty}
                                  step="1"
                                  value={pick.qty || 1}
                                  onChange={(e) => {
                                    let v = parseInt(e.target.value, 10) || 1;
                                    if (v < 1) v = 1;
                                    if (v > stockQty) v = stockQty;
                                    updateBulkPick(cat, { qty: v });
                                  }}
                                  className="h-6 w-14 text-xs text-center font-mono"
                                  data-testid={`psa-bulk-qty-${cat}`}
                                />
                                <span className="text-zinc-500">/ {stockQty}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-end gap-2">
                  {selectedCount > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setBulkPicks({})}
                      className="h-7 text-xs text-zinc-600"
                      data-testid="psa-bulk-clear"
                    >
                      Resetează selecțiile
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    onClick={addBulkPicksToStaged}
                    disabled={selectedCount === 0}
                    className="h-7 text-xs bg-cyan-600 hover:bg-cyan-700"
                    data-testid="psa-bulk-add-all"
                  >
                    <Plus size={12} className="mr-1"/> Adaugă tot în coadă ({selectedCount})
                  </Button>
                </div>
              </div>
            );
          })()}

          {/* Lista items adăugate în coadă */}
          {staged.length > 0 && (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-md p-2.5 space-y-1.5" data-testid="psa-staged-list">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-900">
                ✅ {staged.length} item{staged.length === 1 ? "" : "e"} în coadă pentru predare:
              </div>
              <ul className="space-y-1">
                {staged.map((s, idx) => (
                  <li key={s._id} className="flex items-center justify-between gap-2 bg-white px-2 py-1.5 rounded border border-emerald-200 text-xs" data-testid={`psa-staged-row-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold">{PSA_LABEL[s.cat] || s.cat}</span>
                      {s.serial && <span className="font-mono text-zinc-500 ml-1.5">{s.serial}</span>}
                      {s.size && <span className="text-cyan-700 ml-1.5">măs. {s.size}</span>}
                      {s.model && <span className="text-blue-700 ml-1.5">({s.model})</span>}
                      {s.qty && parseInt(s.qty, 10) > 1 && <span className="text-zinc-700 ml-1.5">× {s.qty}</span>}
                      {s._location && <span className="text-blue-700 ml-1.5">· 📍 {s._location}</span>}
                      {s.price && <span className="text-emerald-700 ml-1.5">{parseFloat(s.price).toFixed(2)} CHF</span>}
                      {s.mode === "existing" && <span className="text-zinc-400 ml-1.5 italic">existent</span>}
                      {s.mode === "new" && <span className="text-emerald-700 ml-1.5 italic">nou în inventar</span>}
                      {s.cat === "echipament_complet" && <span className="text-purple-700 ml-1.5 font-bold">→ creează 3 items</span>}
                    </div>
                    <button type="button" onClick={() => removeFromStaged(s._id)} className="text-red-500 hover:text-red-700 p-0.5" title="Elimină din listă" data-testid={`psa-staged-remove-${idx}`}>
                      <XIcon size={14}/>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* === SECȚIUNE NOUĂ: Selectare ECHIPAMENT din inventar === */}
          {Object.values(equipCatalog.items_by_category || {}).some((arr) => arr.length > 0) && (
            <div className="border-2 border-indigo-400 rounded-md p-3 bg-gradient-to-br from-indigo-50 to-purple-50 space-y-2" data-testid="equip-pick-block">
              <div className="text-xs font-bold uppercase tracking-wider text-indigo-900 flex items-center justify-between gap-2">
                <span>⚙ Adaugă ECHIPAMENT din inventar</span>
                <span className="text-[10px] text-indigo-700 font-normal">
                  ({Object.values(equipCatalog.items_by_category).reduce((s, a) => s + a.length, 0)} disponibile)
                </span>
              </div>
              <div className="grid grid-cols-[1fr_80px_auto] gap-2 items-end">
                <div>
                  <Label className="text-[10px] text-indigo-800">Alege echipament</Label>
                  <div className="relative mb-1">
                    <Input
                      type="text"
                      value={equipSearch}
                      onChange={(e) => setEquipSearch(e.target.value)}
                      placeholder="🔍 Caută (nume, serial, categorie)..."
                      className="h-7 text-xs pr-6"
                      data-testid="equip-search"
                    />
                    {equipSearch && (
                      <button type="button" onClick={() => setEquipSearch("")} className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 text-xs" title="Șterge căutarea">✕</button>
                    )}
                  </div>
                  <Select value={equipPick.itemId || "__none__"} onValueChange={(v) => setEquipPick((s) => ({ ...s, itemId: v === "__none__" ? "" : v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="equip-pick-select">
                      <SelectValue placeholder="— Alege echipament —"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Niciun echipament —</SelectItem>
                      {(() => {
                        const q = equipSearch.trim().toLowerCase();
                        let totalShown = 0;
                        const nodes = [];
                        for (const c of (equipCatalog.categories || [])) {
                          const items = (equipCatalog.items_by_category[c.code] || []).filter((it) => {
                            if (!q) return true;
                            const hay = `${it.name || ""} ${it.serial || ""} ${c.code || ""} ${it.location || ""}`.toLowerCase();
                            return hay.includes(q);
                          });
                          if (items.length === 0) continue;
                          totalShown += items.length;
                          nodes.push(
                            <div key={`hdr-${c.code}`} className="text-[10px] text-indigo-600 px-2 pt-1 pb-0.5 uppercase font-semibold">{c.code} ({items.length})</div>
                          );
                          items.forEach((it) => nodes.push(
                            <SelectItem key={it.id} value={it.id}>
                              {it.name}{it.serial ? ` · SN ${it.serial}` : ""}{it.location ? ` · 📍 ${it.location}` : ""}
                            </SelectItem>
                          ));
                        }
                        if (q && totalShown === 0) {
                          nodes.push(<div key="empty" className="text-xs text-zinc-500 italic px-2 py-2">Nimic găsit pentru „{q}"</div>);
                        }
                        return nodes;
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-indigo-800">Cant.</Label>
                  <Input type="number" min={1} value={equipPick.qty} onChange={(e) => setEquipPick((s) => ({ ...s, qty: e.target.value }))} className="h-8 text-xs text-center" data-testid="equip-pick-qty"/>
                </div>
                <Button size="sm" onClick={addEquipToStaged} disabled={!equipPick.itemId} className="bg-indigo-600 hover:bg-indigo-700 h-8" data-testid="equip-pick-add">
                  + Adaugă
                </Button>
              </div>
            </div>
          )}

          {/* Buton: Adaugă NOU în inventar (pentru cazul când nu găsești ce vrei) */}
          <div className="rounded-md border border-dashed border-emerald-400 bg-emerald-50/30 p-2 flex items-center justify-between gap-2">
            <div className="text-xs text-emerald-800">
              <b>Nu găsești un anumit echipament sau PSA în listă?</b> Adaugă-l direct în inventar.
            </div>
            <Button size="sm" onClick={() => setNewInvOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap" data-testid="psa-new-inv-open">
              ➕ Adaugă în inventar
            </Button>
          </div>

          {/* Setup rând curent */}
          <div className="border-2 border-dashed border-cyan-300 rounded-md p-3 space-y-3 bg-cyan-50/30">
            <div className="text-xs font-bold uppercase tracking-wider text-cyan-900">
              ➕ Adaugă PSA în listă
            </div>

            {/* Pas 1: Categorie */}
            <div>
              <Label className="text-xs">1. Categorie</Label>
              <Select value={draft?.cat || "__none__"} onValueChange={(v) => setDraft({ ...draft, cat: v === "__none__" ? "" : v, itemId: "", serial: "", model: "" })}>
                <SelectTrigger data-testid="psa-add-category"><SelectValue placeholder="— Alege categoria —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Alege categoria —</SelectItem>
                  {categories.map((c) => {
                    const cnt = c.code === "echipament_complet" ? 0 : ((catalog.items_by_category || {})[c.code] || []).length;
                    const suffix = c.code === "echipament_complet" ? "(creează 3 items noi)" : (cnt === 0 ? "(stoc 0 — adaugă nou)" : `(${cnt} disp.)`);
                    return (
                      <SelectItem key={c.code} value={c.code} data-testid={`psa-add-category-opt-${c.code}`}>
                        {c.label} {suffix}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Model (doar pentru echipamente respiratorii — NU pentru echipament_complet care are model per parte) */}
            {showModelField && !isCompleteSet && (
              <div className="p-2.5 rounded bg-blue-50 border border-blue-200" data-testid="psa-add-model-block">
                <Label className="text-xs font-bold text-blue-900">2. Model echipament respirator <span className="text-red-600">*</span></Label>
                <div className="flex gap-2 flex-wrap mt-1">
                  {RESPIRATORY_MODELS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setDraft({ ...draft, model: m, modelCustom: "" })}
                      className={`text-xs px-3 py-1.5 rounded border-2 font-semibold transition-colors ${draft.model === m ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-800 border-blue-300 hover:border-blue-500"}`}
                      data-testid={`psa-add-model-${m}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                {draft.model === "Altele" && (
                  <Input
                    value={draft.modelCustom}
                    onChange={(e) => setDraft({ ...draft, modelCustom: e.target.value })}
                    placeholder="Introdu modelul..."
                    className="mt-2"
                    data-testid="psa-add-model-custom"
                  />
                )}
              </div>
            )}

            {/* Pentru echipament complet — formular extins cu 9 părți */}
            {isCompleteSet && (
              <div className="p-2.5 rounded bg-purple-50 border border-purple-200 space-y-2" data-testid="psa-add-complete-set-block">
                <div className="text-[11px] font-bold text-purple-900 uppercase tracking-wider">
                  🛡 Echipament COMPLET — alege un set existent SAU configurează piese noi
                </div>
                {/* Picker — set existent din inventar */}
                <div className="p-2 rounded bg-white border border-purple-300 space-y-1.5" data-testid="psa-complete-set-picker">
                  <Label className="text-[10px] font-bold text-purple-900 uppercase tracking-wider">
                    🎯 Selectează set existent din inventar
                  </Label>
                  {completeSets.length === 0 ? (
                    <div className="text-[11px] italic text-zinc-500">
                      Nu există seturi complete (mască + furtun + curea cu același nr. serie) disponibile în inventar.
                    </div>
                  ) : (
                    <Select
                      value={draft.completeSetId || "_new"}
                      onValueChange={(v) => setDraft({ ...draft, completeSetId: v === "_new" ? "" : v })}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid="psa-complete-set-select">
                        <SelectValue placeholder="Alege un set complet..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_new">➕ Crează piese NOI (nu folosesc din inventar)</SelectItem>
                        {completeSets.map((s) => (
                          <SelectItem key={s.set_id} value={s.set_id}>
                            {s.model ? `${s.model} ` : ""}#{s.serial}{s.masca_size ? ` · ${s.masca_size}` : ""}{s.location ? ` · 📍 ${s.location}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {draft.completeSetId && (
                    <div className="text-[10px] text-emerald-700 font-semibold">
                      ✓ La adăugare, cele 3 piese existente vor fi scoase din locația curentă și asignate persoanei.
                    </div>
                  )}
                </div>

                {/* Dacă utilizatorul a ales un set existent, ascundem configurarea piesă-cu-piesă */}
                {!draft.completeSetId && (
                  <>
                <div className="text-[10px] text-purple-800 italic">
                  Toate piesele sunt bifate implicit. Dacă persoana are deja unele (ex: mască), debifează-le ca să sari peste. La „Adaugă în listă" se vor crea N items în inventar și asignate persoanei.
                </div>
                <ul className="space-y-2">
                  {COMPLETE_SET_PARTS.map((p) => {
                    const ps = draft.parts[p.cat] || {};
                    return (
                      <li
                        key={p.cat}
                        className={`p-2 rounded border ${ps.enabled ? "bg-white border-purple-300" : "bg-zinc-50 border-zinc-200 opacity-60"}`}
                        data-testid={`psa-complete-part-${p.cat}`}
                      >
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={ps.enabled}
                              onChange={(e) => updatePart(p.cat, { enabled: e.target.checked })}
                              data-testid={`psa-complete-part-${p.cat}-toggle`}
                              className="w-4 h-4 accent-purple-600"
                            />
                            <PSACategoryIcon cat={p.cat} size={18} className="text-purple-700"/>
                            <span className="text-sm font-semibold">{p.label}</span>
                          </label>
                          {p.required && <span className="text-[9px] uppercase font-bold text-amber-600" title="Recomandat, dar îl poți debifa dacă persoana îl are deja">recomandat</span>}
                          {!p.required && <span className="text-[9px] uppercase font-bold text-zinc-500">opțional</span>}
                        </div>
                        {ps.enabled && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {p.hasName && (
                              <Input
                                className="text-xs h-8 md:col-span-2"
                                placeholder="Denumire produs (ex: Cască protecție)"
                                value={ps.name}
                                onChange={(e) => updatePart(p.cat, { name: e.target.value })}
                                data-testid={`psa-complete-${p.cat}-name`}
                              />
                            )}
                            {p.hasSerial && (
                              <Input
                                className="text-xs h-8 font-mono md:col-span-2"
                                placeholder="Nr. de serie"
                                value={ps.serial}
                                onChange={(e) => updatePart(p.cat, { serial: e.target.value })}
                                data-testid={`psa-complete-${p.cat}-serial`}
                              />
                            )}
                            {p.hasModel && (
                              <div className="md:col-span-2">
                                <div className="flex gap-1 flex-wrap">
                                  {RESPIRATORY_MODELS.map((m) => (
                                    <button
                                      key={m}
                                      type="button"
                                      onClick={() => updatePart(p.cat, { model: m, modelCustom: m === "Altele" ? ps.modelCustom : "" })}
                                      className={`text-[10px] px-2 py-1 rounded border font-semibold ${ps.model === m ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-800 border-blue-300"}`}
                                      data-testid={`psa-complete-${p.cat}-model-${m}`}
                                    >
                                      {m}
                                    </button>
                                  ))}
                                </div>
                                {ps.model === "Altele" && (
                                  <Input
                                    className="text-xs h-8 mt-1"
                                    placeholder="Introdu model custom..."
                                    value={ps.modelCustom}
                                    onChange={(e) => updatePart(p.cat, { modelCustom: e.target.value })}
                                    data-testid={`psa-complete-${p.cat}-model-custom`}
                                  />
                                )}
                              </div>
                            )}
                            {p.hasSize && (
                              <Input
                                className="text-xs h-8"
                                placeholder="Măsură (S/M/L/43...)"
                                value={ps.size}
                                onChange={(e) => updatePart(p.cat, { size: e.target.value })}
                                data-testid={`psa-complete-${p.cat}-size`}
                              />
                            )}
                            {p.hasQty && (
                              <Input
                                type="text"
                                className="text-xs h-8"
                                placeholder='Cantitate (buc) — scrie "0" sau "fără" ca să sari'
                                value={ps.qty}
                                onChange={(e) => updatePart(p.cat, { qty: e.target.value })}
                                data-testid={`psa-complete-${p.cat}-qty`}
                              />
                            )}
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              className="text-xs h-8"
                              placeholder="Preț unitar CHF (opțional)"
                              value={ps.price}
                              onChange={(e) => updatePart(p.cat, { price: e.target.value })}
                              data-testid={`psa-complete-${p.cat}-price`}
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                  </>
                )}
              </div>
            )}

            {/* Pentru categorii normale — alegere existent SAU creare nou */}
            {draft?.cat && !isCompleteSet && (
              <>
                <div>
                  <Label className="text-xs">{showModelField ? "3." : "2."} Mod de adăugare</Label>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, mode: "existing", serial: "" })}
                      className={`flex-1 text-xs px-3 py-2 rounded border-2 font-semibold ${draft.mode === "existing" ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-cyan-800 border-cyan-300"}`}
                      data-testid="psa-add-mode-existing"
                    >
                      Selectează din stoc ({availableForCat.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, mode: "new", itemId: "" })}
                      className={`flex-1 text-xs px-3 py-2 rounded border-2 font-semibold ${draft.mode === "new" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-800 border-emerald-300"}`}
                      data-testid="psa-add-mode-new"
                    >
                      Crează item nou
                    </button>
                  </div>
                </div>

                {draft.mode === "existing" && availableForCat.length > 0 && (
                  <>
                    <Select value={draft.itemId || "__none__"} onValueChange={(v) => setDraft({ ...draft, itemId: v === "__none__" ? "" : v, qty: 1 })}>
                      <SelectTrigger data-testid="psa-add-item"><SelectValue placeholder="— Alege produsul existent —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Alege produsul existent —</SelectItem>
                        {availableForCat.map((it) => {
                          const qty = parseFloat(it.quantity) || 1;
                          const stockLabel = qty > 1 ? ` · 📦 ${qty} buc.` : "";
                          const locLabel = it.location ? ` · 📍 ${it.location}` : "";
                          return (
                            <SelectItem key={it.id} value={it.id}>
                              {it.name}{it.psa_size ? ` · ${it.psa_size}` : ""}{it.serial ? ` · SN ${it.serial}` : ""}{stockLabel}{locLabel}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {/* Input pentru cantitate — apare doar pentru PSA cu mărime și stoc > 1 */}
                    {(() => {
                      const selectedItem = availableForCat.find((it) => it.id === draft.itemId);
                      if (!selectedItem) return null;
                      const stockQty = parseFloat(selectedItem.quantity) || 1;
                      const isRespiratory = ["masca", "furtun", "curea"].includes(selectedItem.psa_category);
                      if (isRespiratory || stockQty <= 1) return null;
                      return (
                        <div className="mt-2 p-2.5 rounded bg-emerald-50 border border-emerald-300" data-testid="psa-split-qty-block">
                          <Label className="text-xs font-semibold text-emerald-900">
                            📦 Câte bucăți predai din stoc?
                          </Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              type="number"
                              min="1"
                              max={stockQty}
                              step="1"
                              value={draft.qty || 1}
                              onChange={(e) => {
                                let v = parseInt(e.target.value) || 1;
                                if (v < 1) v = 1;
                                if (v > stockQty) v = stockQty;
                                setDraft({ ...draft, qty: v });
                              }}
                              className="w-24 font-mono font-bold text-center"
                              data-testid="psa-split-qty-input"
                            />
                            <span className="text-xs text-zinc-600">
                              din <strong>{stockQty}</strong> buc. disponibile{selectedItem.psa_size ? ` (mărime ${selectedItem.psa_size})` : ""}
                            </span>
                          </div>
                          <div className="text-[10px] text-zinc-500 mt-1.5 italic">
                            La predare, stocul scade automat de la <strong>{stockQty}</strong> la <strong>{stockQty - (parseInt(draft.qty) || 1)}</strong>, iar angajatul primește <strong>{parseInt(draft.qty) || 1}</strong> buc. în fișa sa.
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}

                {draft.mode === "existing" && availableForCat.length === 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    ⚠ Niciun produs disponibil în stoc pentru această categorie. Folosește „Crează item nou".
                  </div>
                )}

                {draft.mode === "new" && (
                  <div className="space-y-2 p-2.5 rounded bg-emerald-50 border border-emerald-300">
                    <div>
                      <Label className="text-xs">Nr. serie <span className="text-red-600">*</span></Label>
                      <Input
                        value={draft.serial}
                        onChange={(e) => setDraft({ ...draft, serial: e.target.value })}
                        placeholder="ex: MSK-2026-001"
                        className="font-mono"
                        data-testid="psa-add-new-serial"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Măsură (S/M/L/43...)</Label>
                        <Input value={draft.size} onChange={(e) => setDraft({ ...draft, size: e.target.value })} data-testid="psa-add-new-size"/>
                      </div>
                      <div>
                        <Label className="text-xs">Preț CHF</Label>
                        <Input type="number" step="0.01" min="0" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid="psa-add-new-price"/>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Notă */}
            {draft?.cat && (draft.itemId || draft.serial) && (
              <div>
                <Label className="text-xs">Notă predare (opțional)</Label>
                <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="ex: Predat în prima zi" data-testid="psa-add-note"/>
              </div>
            )}

            <Button
              type="button"
              onClick={addDraftToStaged}
              disabled={!draft?.cat || (!isCompleteSet && !draft.itemId && !draft.serial)}
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              data-testid="psa-add-to-list-btn"
            >
              <Plus size={14} className="mr-1"/> Adaugă în listă
            </Button>
          </div>

          <DialogFooter className="gap-2 sm:justify-between flex-wrap">
            <Button variant="outline" onClick={() => { setAddOpen(false); setStaged([]); resetDraft(); }} disabled={saving}>Anulează</Button>
            <Button
              onClick={submitAll}
              disabled={saving || staged.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 font-bold"
              data-testid="psa-submit-all"
            >
              {saving ? "Se predă..." : `📤 Predă ${staged.length} item${staged.length === 1 ? "" : "e"} către ${personName}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: PICKUP PSA — preia de la angajat, fiecare cu destinație proprie */}
      <PSAPickupDialog
        open={pickupOpen}
        busy={pickupBusy}
        personId={personId}
        personName={personName}
        assigned={assigned}
        persons={persons}
        locations={locations}
        pickupTargets={pickupTargets}
        bulkDest={pickupBulkDest}
        pickupDate={pickupDate}
        pickupBy={pickupBy}
        PSACategoryIcon={PSACategoryIcon}
        onOpenChange={(v) => { if (!pickupBusy) setPickupOpen(v); }}
        onSetBulkDest={setPickupBulkDest}
        onSetPickupDate={setPickupDate}
        onSetPickupBy={setPickupBy}
        onApplyBulk={applyPickupBulkDest}
        onSelectAll={selectAllPickup}
        onUpdateRow={updatePickupRow}
        onSubmit={submitPickup}
        onCancel={() => setPickupOpen(false)}
      />

      {/* Dialog: Casare PSA */}
      <PSAScrapDialog
        target={scrapTarget}
        busy={scrapBusy}
        reason={scrapReason}
        date={scrapDate}
        note={scrapNote}
        scrapReasons={SCRAP_REASONS}
        psaLabels={PSA_LABEL}
        canScrapAny={canScrapAny}
        onClose={() => setScrapTarget(null)}
        onSetReason={setScrapReason}
        onSetDate={setScrapDate}
        onSetNote={setScrapNote}
        onConfirm={confirmScrap}
      />

      {/* Dialog: Restituire PSA cu detalii (stare + defect + imagine) */}
      <Dialog open={!!returnTarget} onOpenChange={(v) => { if (!v) setReturnTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 size={18} className="text-blue-600"/>
              Restituire PSA
            </DialogTitle>
          </DialogHeader>
          {returnTarget && (
            <div className="space-y-3 text-sm">
              <div className="bg-cyan-50 border border-cyan-200 rounded p-2 text-cyan-900">
                <div className="font-semibold">{returnTarget.name}</div>
                {returnTarget.serial && <div className="text-[10px] font-mono">SN: {returnTarget.serial}</div>}
              </div>
              <div>
                <Label className="text-xs font-bold">Data restituirii</Label>
                <Input type="date" value={returnDate} max={new Date().toISOString().slice(0,10)} onChange={(e) => setReturnDate(e.target.value)} data-testid="psa-return-date"/>
              </div>
              <div>
                <Label className="text-xs font-bold mb-1 block">Stare la restituire</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setReturnCondition("bun")}
                    className={`px-3 py-2 rounded-md border-2 font-bold text-xs transition-all ${returnCondition === "bun" ? "bg-emerald-500 text-white border-emerald-600" : "bg-white text-zinc-600 border-zinc-200 hover:border-emerald-300"}`}
                    data-testid="psa-return-cond-bun"
                  >
                    ✅ ÎN STARE BUNĂ
                  </button>
                  <button
                    type="button"
                    onClick={() => setReturnCondition("defect")}
                    className={`px-3 py-2 rounded-md border-2 font-bold text-xs transition-all ${returnCondition === "defect" ? "bg-red-500 text-white border-red-600" : "bg-white text-zinc-600 border-zinc-200 hover:border-red-300"}`}
                    data-testid="psa-return-cond-defect"
                  >
                    🔴 DEFECT
                  </button>
                </div>
              </div>
              {returnCondition === "defect" && (
                <>
                  <div>
                    <Label className="text-xs font-bold">Descriere defecțiune <span className="text-red-500">*</span></Label>
                    <textarea
                      value={returnDefectNote}
                      onChange={(e) => setReturnDefectNote(e.target.value)}
                      rows={3}
                      className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm"
                      placeholder="Ex: Furtun rupt la conexiune, mască spartă în partea dreaptă..."
                      data-testid="psa-return-defect-note"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold">Imagine defect (opțional)</Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => uploadDefectImage(e.target.files?.[0])}
                      className="text-xs w-full mt-1"
                      data-testid="psa-return-defect-image"
                    />
                    {returnDefectFileName && (
                      <div className="text-[10px] text-emerald-700 mt-1">✅ {returnDefectFileName}</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnTarget(null)} disabled={returnBusy}>Anulează</Button>
            <Button
              onClick={confirmReturn}
              disabled={returnBusy || (returnCondition === "defect" && !returnDefectNote.trim())}
              className={returnCondition === "defect" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
              data-testid="psa-return-confirm"
            >
              {returnBusy ? "Se salvează..." : `Restituie ${returnCondition === "defect" ? "ca DEFECT" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editare PSA */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>✏️ Editare PSA</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-2.5 text-sm">
              <div>
                <Label className="text-xs font-bold">Nume <span className="text-red-500">*</span></Label>
                <Input value={editForm.name || ""} onChange={(e) => setEditForm((s) => ({ ...s, name: e.target.value }))} data-testid="psa-edit-name"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-bold">Mărime</Label>
                  <Input value={editForm.psa_size || ""} onChange={(e) => setEditForm((s) => ({ ...s, psa_size: e.target.value }))} placeholder="Ex: 43, L, M..." data-testid="psa-edit-size"/>
                </div>
                <div>
                  <Label className="text-xs font-bold">Serial (SN)</Label>
                  <Input value={editForm.serial || ""} onChange={(e) => setEditForm((s) => ({ ...s, serial: e.target.value }))} data-testid="psa-edit-serial"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-bold">Model / Furnizor</Label>
                  <Input value={editForm.supplier || ""} onChange={(e) => setEditForm((s) => ({ ...s, supplier: e.target.value }))} placeholder="Ex: Sundström, 3M" data-testid="psa-edit-supplier"/>
                </div>
                <div>
                  <Label className="text-xs font-bold">Preț (CHF)</Label>
                  <Input type="number" step="0.01" min={0} value={editForm.price_chf ?? ""} onChange={(e) => setEditForm((s) => ({ ...s, price_chf: e.target.value }))} data-testid="psa-edit-price"/>
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold">Cantitate</Label>
                <Input type="number" step="1" min={1} value={editForm.quantity ?? 1} onChange={(e) => setEditForm((s) => ({ ...s, quantity: e.target.value }))} data-testid="psa-edit-qty"/>
              </div>
              <div>
                <Label className="text-xs font-bold">📅 Data alocării</Label>
                <Input
                  type="date"
                  value={editForm.assigned_at || ""}
                  onChange={(e) => setEditForm((s) => ({ ...s, assigned_at: e.target.value }))}
                  max={new Date().toISOString().slice(0, 10)}
                  data-testid="psa-edit-assigned-at"
                />
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  Data la care PSA-ul a fost predat angajatului
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold">👤 Alocat de</Label>
                <Select
                  value={editForm.assigned_by_id || "__none__"}
                  onValueChange={(v) => {
                    if (v === "__none__") {
                      setEditForm((s) => ({ ...s, assigned_by_id: "", assigned_by: "" }));
                    } else {
                      const p = allPersons.find((x) => x.id === v);
                      setEditForm((s) => ({
                        ...s,
                        assigned_by_id: v,
                        assigned_by: p ? `${p.prenume || ""} ${p.nume || ""}`.trim() : "",
                      }));
                    }
                  }}
                >
                  <SelectTrigger className="mt-1 h-8 text-sm" data-testid="psa-edit-assigned-by">
                    {(() => {
                      const sel = allPersons.find((x) => x.id === editForm.assigned_by_id);
                      if (sel) {
                        const init = getInitials(sel.prenume, sel.nume);
                        const clr = colorForKey(sel.id || `${sel.nume}${sel.prenume}`);
                        return (
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold ${clr}`}>
                              {init}
                            </span>
                            <span className="truncate">{`${sel.prenume || ""} ${sel.nume || ""}`.trim()}</span>
                          </div>
                        );
                      }
                      return <SelectValue placeholder={editForm.assigned_by || "— Alege persoana —"}/>;
                    })()}
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— Fără —</SelectItem>
                    {[...allPersons]
                      .sort((a, b) => `${a.nume || ""} ${a.prenume || ""}`.localeCompare(`${b.nume || ""} ${b.prenume || ""}`))
                      .map((p) => {
                        const init = getInitials(p.prenume, p.nume);
                        const clr = colorForKey(p.id || `${p.nume}${p.prenume}`);
                        return (
                          <SelectItem key={p.id} value={p.id}>
                            <div className="flex items-center gap-2">
                              <span className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-white text-[10px] font-bold ${clr}`}>
                                {init}
                              </span>
                              <span>{p.nume} {p.prenume || ""}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  Persoana din echipa care a predat efectiv PSA-ul angajatului
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold">Notă</Label>
                <textarea rows={2} value={editForm.note || ""} onChange={(e) => setEditForm((s) => ({ ...s, note: e.target.value }))} className="w-full border border-zinc-300 rounded px-2 py-1.5 text-sm" data-testid="psa-edit-note"/>
              </div>
              <div>
                <Label className="text-xs font-bold">Notă predare (recipient)</Label>
                <Input value={editForm.recipient_note || ""} onChange={(e) => setEditForm((s) => ({ ...s, recipient_note: e.target.value }))} data-testid="psa-edit-recnote"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={editBusy}>Anulează</Button>
            <Button onClick={confirmEdit} disabled={editBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="psa-edit-confirm">
              {editBusy ? "Se salvează..." : "💾 Salvează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Istoric PSA per item */}
      <Dialog open={!!historyTarget} onOpenChange={(v) => { if (!v) setHistoryTarget(null); }}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>📜 Istoric echipament</DialogTitle>
          </DialogHeader>
          {historyTarget && (
            <div className="text-sm">
              <div className="mb-2 p-2 bg-cyan-50 border border-cyan-200 rounded text-cyan-900">
                <div className="font-bold">{historyTarget.name}</div>
                {historyTarget.serial && <div className="text-[10px] font-mono">SN: {historyTarget.serial}</div>}
              </div>
              <div className="overflow-y-auto max-h-[55vh] pr-1">
                {historyLoading ? (
                  <div className="text-center text-zinc-500 italic py-8">Se încarcă...</div>
                ) : historyRows.length === 0 ? (
                  <div className="text-center text-zinc-500 italic py-8">Fără istoric</div>
                ) : (
                  <ul className="space-y-2">
                    {historyRows.map((h) => {
                      const isReturn = h.action === "return";
                      const isMove = h.action === "move";
                      const isUpdate = h.action === "update";
                      const icon = isReturn ? "↩️" : isMove ? "🔀" : isUpdate ? "✏️" : "📝";
                      const bg = isReturn && h.condition === "defect" ? "bg-red-50 border-red-200" : isReturn ? "bg-emerald-50 border-emerald-200" : "bg-zinc-50 border-zinc-200";
                      return (
                        <li key={h.id || h.ts} className={`border rounded p-2 ${bg}`} data-testid={`psa-hist-${h.id}`}>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-lg">{icon}</span>
                            <span className="font-semibold uppercase">{h.action}</span>
                            <span className="text-zinc-500 font-mono">{h.ts ? new Date(h.ts).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                            {h.user && <span className="ml-auto text-zinc-600">de <b>{h.user}</b></span>}
                          </div>
                          {isReturn && (
                            <div className="mt-1 text-[11px]">
                              <span className={`font-bold ${h.condition === "defect" ? "text-red-700" : "text-emerald-700"}`}>
                                {h.condition === "defect" ? "🔴 DEFECT" : "✅ În stare bună"}
                              </span>
                              {h.from_assignee && <span className="ml-2 text-zinc-600">de la <b>{h.from_assignee}</b></span>}
                              {h.defect_note && <div className="mt-0.5 text-red-800">📝 {h.defect_note}</div>}
                              {h.defect_image_file_id && (
                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${h.defect_image_file_id}`} target="_blank" rel="noreferrer" className="inline-block mt-1">
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${h.defect_image_file_id}`} alt="defect" className="max-h-20 rounded border border-red-300"/>
                                </a>
                              )}
                            </div>
                          )}
                          {isMove && (
                            <div className="mt-1 text-[11px] text-zinc-700">
                              {h.from_assignee && <span>de la <b>{h.from_assignee}</b> </span>}
                              {h.to_assignee && <span>→ către <b>{h.to_assignee}</b></span>}
                              {h.reason && <div className="text-[10px] text-zinc-500 italic mt-0.5">{h.reason}</div>}
                            </div>
                          )}
                          {isUpdate && h.changes && (
                            <div className="mt-1 text-[10px] text-zinc-700 space-y-0.5">
                              {Object.entries(h.changes).slice(0, 5).map(([field, ch]) => (
                                <div key={field}><b>{field}:</b> <span className="line-through text-zinc-400">{String(ch?.from ?? "—")}</span> → {String(ch?.to ?? "—")}</div>
                              ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryTarget(null)}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog: Adaugă item NOU direct în inventar (din contextul PSA) */}
      <Dialog open={newInvOpen} onOpenChange={(v) => { if (!newInvBusy) setNewInvOpen(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>➕ Adaugă în inventar</DialogTitle>
          </DialogHeader>
          <div className="space-y-2.5 text-sm">
            <div>
              <Label className="text-xs font-bold">Nume produs <span className="text-red-500">*</span></Label>
              <Input value={newInvForm.name} onChange={(e) => setNewInvForm((s) => ({ ...s, name: e.target.value }))} placeholder="Ex: Cască sudură automată" autoFocus data-testid="new-inv-name"/>
            </div>
            <div>
              <Label className="text-xs font-bold mb-1 block">Tip</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setNewInvForm((s) => ({ ...s, item_type: "echipament" }))} className={`px-2 py-2 rounded border-2 text-xs font-bold ${newInvForm.item_type === "echipament" ? "bg-indigo-500 text-white border-indigo-600" : "bg-white text-zinc-600 border-zinc-200"}`} data-testid="new-inv-type-equip">⚙ ECHIPAMENT</button>
                <button type="button" onClick={() => setNewInvForm((s) => ({ ...s, item_type: "psa" }))} className={`px-2 py-2 rounded border-2 text-xs font-bold ${newInvForm.item_type === "psa" ? "bg-cyan-500 text-white border-cyan-600" : "bg-white text-zinc-600 border-zinc-200"}`} data-testid="new-inv-type-psa">🦺 PSA</button>
              </div>
            </div>
            {newInvForm.item_type === "psa" ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-bold">Categorie PSA</Label>
                  <Select value={newInvForm.psa_category} onValueChange={(v) => setNewInvForm((s) => ({ ...s, psa_category: v }))}>
                    <SelectTrigger className="h-8 text-xs" data-testid="new-inv-psa-cat"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {(catalog.categories || []).map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.label || c.code}</SelectItem>
                      ))}
                      <SelectItem value="altele">Altele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold">Mărime</Label>
                  <Input value={newInvForm.psa_size} onChange={(e) => setNewInvForm((s) => ({ ...s, psa_size: e.target.value }))} placeholder="43, L, M..." data-testid="new-inv-psa-size"/>
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-xs font-bold">Categorie echipament</Label>
                <Input value={newInvForm.equipment_category} onChange={(e) => setNewInvForm((s) => ({ ...s, equipment_category: e.target.value }))} placeholder="Ex: unelte, mașini, altele" data-testid="new-inv-equip-cat"/>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold">Cantitate</Label>
              <Input type="number" min={1} step="1" value={newInvForm.quantity} onChange={(e) => setNewInvForm((s) => ({ ...s, quantity: e.target.value }))} data-testid="new-inv-qty"/>
            </div>
            <div>
              <Label className="text-xs font-bold">Notă (opțional)</Label>
              <Input value={newInvForm.note} onChange={(e) => setNewInvForm((s) => ({ ...s, note: e.target.value }))} data-testid="new-inv-note"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewInvOpen(false)} disabled={newInvBusy}>Anulează</Button>
            <Button onClick={createNewInventoryItem} disabled={newInvBusy || !newInvForm.name.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-inv-confirm">
              {newInvBusy ? "Se creează..." : "✅ Adaugă în inventar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
